import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import { useGetCampaignCodesQuery } from "@/redux/rtk/features/campaignCode/campaignCodeApi";
import { useBulkUpdateLeadDetailsMutation } from "@/redux/rtk/features/leads/leadsApi";
import { Button, Form, Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import { COUNTRIES } from "@/utils/countries";

export default function BulkUpdateLeadDetails({
  selectedRowKeys,
  onClose,
  clearSelection,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [bulkUpdateLeadDetails, { isLoading }] =
    useBulkUpdateLeadDetailsMutation();

  const { data: majors, isLoading: majorLoading } = useGetMajorsQuery();
  const { data: degrees, isLoading: degreeLoading } = useGetDegreesQuery();
  const { data: campaignCodes, isLoading: campaignCodeLoading } = useGetCampaignCodesQuery();

  const onFinish = async (values) => {
    const payload = {
      ids: selectedRowKeys,
      majorId: values.majorId,
      campaignCode: values.campaignCode,
      adId: values.adId,
      degree: values.degree,
      country: values.country,
      countryOfStudy: values.country_of_study,
      description: values.description,
    };
    // Filter out undefined/null values
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null)
    );

    const resp = await bulkUpdateLeadDetails(cleanPayload);
    if (resp.data) {
      form.resetFields();
      clearSelection();
      onClose();
    }
  };

  return (
    <UserPrivateComponent permission="update-lead">
      <div className="mt-5">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="flex flex-col gap-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label={t("lead.major")} name="majorId">
              <Select
                loading={majorLoading}
                placeholder={t("lead.selectMajor")}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {majors?.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.degree")} name="degree">
              <Select
                allowClear
                loading={degreeLoading}
                showSearch
                placeholder={t("lead.selectDegree")}
              >
                {degrees?.map((item) => (
                  <Select.Option key={item.id} value={item.name}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.country")} name="country">
              <Select
                style={{ width: "100%" }}
                allowClear
                showSearch
                placeholder={t("lead.country")}>
                {COUNTRIES.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.country_of_study")} name="country_of_study">
              <Select
                style={{ width: "100%" }}
                allowClear
                showSearch
                placeholder={t("lead.country_of_study")}>
                {COUNTRIES.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.campaignCode")} name="campaignCode">
              <Select
                loading={campaignCodeLoading}
                placeholder={t("lead.selectCampaignCode") || "Select campaign code"}
                showSearch
                allowClear
              >
                {campaignCodes?.map((item) => (
                  <Select.Option key={item.id} value={item.name}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={t("lead.adId")} name="adId">
              <Input placeholder={t("lead.enterAdId")} />
            </Form.Item>
          </div>

          <Form.Item label={t("lead.description")} name="description">
            <Input.TextArea rows={4} placeholder={t("lead.description")} />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {t("common.update")}
            </Button>
          </div>
        </Form>
      </div>
    </UserPrivateComponent>
  );
}
